import React, { useCallback, useEffect, useRef } from "react";
import {
  IconTrash,
  IconPencil,
} from "@tabler/icons";
import _ from "lodash";
import { Box } from "@mantine/core";
import CreatableTypeSelect from "./creatableTypeSelect";
import PhoneEntryFields from "./phoneEntryFields";
import InlineContactList from "./inlineContactList";
import { DATA_PHONE_TYPE } from "../data/options";
import { useCellRender } from "../hooks/useCellRender";
import { formatPhoneDisplay, isPhoneComplete } from "../helpers/phoneUtils";
import { legacyPhoneFieldsFromList } from "../helpers/contactListLegacySync";
import { saveFormFields } from "../helpers/formFieldSave";
import { useTranslation } from "react-i18next";

const EMPTY_PHONE_DRAFT = {
  type: "mobile",
  countryCode: "852",
  areaCode: "",
  number: "",
  tags: [],
  isDefault: false,
};

const TYPE_COLUMN_WIDTH = 160;

const isPhoneDraftEmpty = (values) =>
  !String(values?.number ?? "").trim() && !String(values?.areaCode ?? "").trim();

const PhoneNumberList = ({ form: mainForm, name, readOnly, ...props }) => {
  const { t } = useTranslation();
  const phones = _.get(mainForm.values, name) ?? [];
  const [cellRender] = useCellRender();
  const listRef = useRef(null);

  const columns = [
    {
      field: "type",
      headerName: "Type",
      width: TYPE_COLUMN_WIDTH,
      cellRender: (col, data) => cellRender.ContactType(data.type),
    },
    {
      field: "number",
      headerName: "Number",
      fill: true,
      minWidth: 320,
      cellRender: (col, data) => formatPhoneDisplay(data),
    },
    {
      field: "_actions",
      width: 72,
      isAction: true,
      actions: [
        {
          name: "edit",
          label: "Edit",
          icon: IconPencil,
          props: {},
        },
        {
          name: "delete",
          label: "Delete",
          icon: IconTrash,
          props: {},
        },
      ],
    },
  ];

  const validate = (values) => {
    const errors = {};
    if (!isPhoneComplete(values)) {
      errors.number = t("Required");
    }
    return errors;
  };

  const normalizeEntry = (values, existingRow) => {
    const id = existingRow?._id ?? values?._id;
    return {
      ...(id != null && id !== "" ? { _id: id } : {}),
      type: values.type?.trim() || "mobile",
      countryCode: values.countryCode ?? "852",
      areaCode: values.areaCode ?? "",
      number: values.number?.trim() ?? "",
      tags: existingRow?.tags ?? [],
      isDefault: Boolean(existingRow?.isDefault),
    };
  };

  const onChange = (values) => {
    saveFormFields(
      mainForm,
      {
        [name]: values,
        ...legacyPhoneFieldsFromList(values, mainForm.values),
      },
      { notify: true }
    );
  };

  const flushIntoForm = useCallback(() => {
    const result = listRef.current?.commitPending?.() ?? { ok: true };
    if (!result.ok) return false;
    if (!result.rows) return { ok: true };
    const updates = {
      [name]: result.rows,
      ...legacyPhoneFieldsFromList(result.rows, mainForm.values),
    };
    for (const [field, value] of Object.entries(updates)) {
      mainForm.setFieldValue(field, value);
    }
    return { ok: true, updates };
  }, [mainForm, name]);

  useEffect(() => {
    if (typeof mainForm.registerPendingEditor !== "function") return undefined;
    mainForm.registerPendingEditor(flushIntoForm);
    return () => mainForm.unregisterPendingEditor(flushIntoForm);
  }, [flushIntoForm, mainForm]);

  const renderFields = (form) => (
    <>
      <td
        style={{
          verticalAlign: "top",
          width: TYPE_COLUMN_WIDTH,
          minWidth: TYPE_COLUMN_WIDTH,
        }}
      >
          <CreatableTypeSelect
            form={form}
            name="type"
            items={DATA_PHONE_TYPE}
            required
            size="xs"
          />
        </td>
        <td style={{ verticalAlign: "top" }}>
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <PhoneEntryFields
              form={form}
              required
              placeholder="61210001"
              compact
              size="xs"
            />
          </Box>
        </td>
      </>
  );

  return (
    <InlineContactList
      ref={listRef}
      rows={phones}
      readOnly={readOnly}
      onChange={onChange}
      columns={columns}
      emptyDraft={EMPTY_PHONE_DRAFT}
      validate={validate}
      normalizeEntry={normalizeEntry}
      isDraftEmpty={isPhoneDraftEmpty}
      renderFields={renderFields}
      {...props}
    />
  );
};

export default PhoneNumberList;
